import React, { useLayoutEffect, useState, useRef } from "react";
import "../../../assets/scss/_horizontal-section.scss";
import PropTypes from "prop-types";
import NavItem from "./HorizontalSection/NavItem";
import { getStatusIcon } from "../../../utils/reportUtils";
import { Alert } from "../../../stories/Alerts/Alert";
import TabItem from "./HorizontalSection/TabItem";

/**
 * HorizontalSection component for displaying a navigable section with items.
 * @returns {JSX.Element} The rendered horizontal section.
 */

const HorizontalSection = ({
  items,
  current,
  setCurrentItem,
  isPartialResult,
  doctor,
  drImg,
  onRegisterOpener,
  reportedDate,
}) => {
  /**
   * Renders the content of the current item based on the selection.
   * @returns {JSX.Element} The content of the currently selected item.
   */

  const [isPartial, setIsPartial] = useState(null);
  const [activeMarkerDetail, setActiveMarkerDetail] = useState(null);
  const navRefs = useRef({});

  useLayoutEffect(() => {
    setIsPartial(isPartialResult());
  }, []);

  useLayoutEffect(() => {
    if (!onRegisterOpener) return;
    const openMarker = (markerCode) => {
      // Find the area that owns this marker
      const targetItem = items.find((item) =>
        item.markers?.some((m) => m.code === markerCode),
      );
      if (!targetItem) return;

      // Find the full marker object
      const fullMarker = targetItem.markers.find((m) => m.code === markerCode);
      if (!fullMarker) return;

      setCurrentItem(targetItem.name);

      setActiveMarkerDetail({
        itemName: targetItem.name,
        marker: fullMarker,
      });

      // Tell the NavItem to open AND switch to detail view
      navRefs.current[targetItem.name]?.openMarkerDetail(fullMarker);
    };

    onRegisterOpener(openMarker);
  }, [items]);

  const status = items[0]?.area_status || items[0]?.status;
  const isResultSection =
    current !== "InsightsSection" && current !== "doctor_notes";
  const hasActiveMarkerDetail = activeMarkerDetail !== null;

  const renderContent = () => {
    const currentItem = items?.find((i) => i.name === current) || items[0];
    return currentItem.content();
  };

  const handleCloseMarkerDetail = () => {
    setActiveMarkerDetail(null);
    if (activeMarkerDetail) {
      navRefs.current[activeMarkerDetail.itemName]?.reset();
    }
  };

  return (
    <div
      className={`horizontal-section-container mt-4 px-3 doctor-container ${
        current === "doctor_notes" ? "result-card" : "w-100"
      } ${current === "InsightsSection" ? "insights-section" : ""}`}
    >
      {current !== "InsightsSection" && (
        <div className={`${isResultSection && "col-12"} `}>
          <aside
            className={`horizontal-section-nav ${isResultSection && "w-100 p-0"}`}
          >
            {current === "doctor_notes" && (
              <TabItem
                key={items[0]?.name}
                item={items[0]}
                status={status}
                isCurrent={current === items[0]?.name}
                setCurrentItem={setCurrentItem}
                getStatusIcon={getStatusIcon}
                doctor={doctor}
                drImg={drImg}
              />
            )}

            {isResultSection &&
              !hasActiveMarkerDetail && (
              // Show all items normally when no marker detail is active
              items
                .filter((item) => !item.hiddenFromNav)
                .filter((item) => item?.name !== "doctor_notes")
                .filter((item) => item?.name !== "InsightsSection")
                .map((item,index) => {
                  const itemStatus = item.area_status || item.status;
                  return (
                    <NavItem
                      key={item.name}
                      index={index}
                      ref={(el) => {
                        navRefs.current[item.name] = el;
                      }}
                      item={item}
                      status={itemStatus}
                      isCurrent={current === item.name}
                      setCurrentItem={setCurrentItem}
                      getStatusIcon={getStatusIcon}
                      isPartial={isPartial}
                      reportedDate={reportedDate}
                      onMarkerDetailOpen={(marker) => {
                        setActiveMarkerDetail({
                          itemName: item.name,
                          marker: marker,
                        });
                      }}
                      onMarkerDetailClose={handleCloseMarkerDetail}
                    />
                  );
                }))}

            {isResultSection &&
              hasActiveMarkerDetail &&

              (() => {
                const activeItem = items.find(
                  (item) => item.name === activeMarkerDetail.itemName,
                );
                if (!activeItem) return null;
                const itemStatus = activeItem.area_status || activeItem.status;
                return (
                  <NavItem
                    key={activeItem.name}
                    ref={(el) => {
                      navRefs.current[activeItem.name] = el;
                    }}
                    item={activeItem}
                    status={itemStatus}
                    isCurrent={true}
                    setCurrentItem={setCurrentItem}
                    getStatusIcon={getStatusIcon}
                    isPartial={isPartial}
                    reportedDate={reportedDate}
                    activeMarkerDetail={activeMarkerDetail.marker}
                    onMarkerDetailClose={handleCloseMarkerDetail}
                  />
                );
              })()}
          </aside>
        </div>
      )}

      {!isResultSection && (
        <div className={current === "InsightsSection" && "col-12"}>
          <main className="horizontal-section-main">
            {renderContent()}
            <div className="healthArea__container">
              {isPartial?.includes(current) && (
                <div className="healthArea__list">
                  <Alert
                    className="partial-alert"
                    variant="light"
                    text={`**${current}** could not be reported.`}
                  />
                </div>
              )}
            </div>
          </main>
        </div>
      )}
    </div>
  );
};

HorizontalSection.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      content: PropTypes.func.isRequired,
      hiddenFromNav: PropTypes.bool,
      area_status: PropTypes.string,
      status: PropTypes.string,
      markers: PropTypes.array,
    }),
  ).isRequired,
  current: PropTypes.string.isRequired,
  setCurrentItem: PropTypes.func.isRequired,
  isPartialResult: PropTypes.func,
  doctor: PropTypes.string,
  drImg: PropTypes.string,
  onRegisterOpener: PropTypes.func,
};

export default HorizontalSection;
